// Eskom PARCC — mechanism → Report-to-Management class (Lindiwe's RTM, Phase 2).
//
// Her RTM Table 1 groups transactions into the classes her document uses (Formal
// Tendering, Modification, Sole Source/single source, Panel, Task orders, Informal
// Tendering - PO). The stored `mechanism` is free text (34 spellings measured), and
// her "Panel" is a judgement not recorded in the data (the panel flag is 0 on ICT
// Q1) — so classification is three layers, first match wins:
//   1. a per-transaction override (report.classOverridesJson[referenceNo]);
//   2. a per-division profile map (profile.classMapJson[`${mechFacet}|${dataset}`]);
//   3. the pure default below.
// Pure; tested in test/features/reports/rtm/rtmClass.test.ts.
import type { Transaction } from '../../../data/types';
import { normalizeMechanismFacet, normalizeDatasetFacet } from '../../../domain/normalizeFacets';

export const RTM_CLASSES = [
  'Formal Tendering',
  'Modification',
  'Sole Source/single source',
  'Panel',
  'Task orders',
  'Informal Tendering - PO',
  'Other',
] as const;
export type RtmClass = (typeof RTM_CLASSES)[number];

/** The pure default class for a transaction (no override / profile map). */
export function rtmClassDefault(t: Pick<Transaction, 'mechanism' | 'dataset' | 'panel'>): RtmClass {
  const dataset = normalizeDatasetFacet(t.dataset);
  if (t.panel === true || dataset === 'Panels') return 'Panel';
  if (dataset === 'Task Order') return 'Task orders';
  const m = normalizeMechanismFacet(t.mechanism).toLowerCase();
  if (/sole source|single source|multiple source/.test(m)) return 'Sole Source/single source';
  if (/modification|contract adjustment|condonation/.test(m)) return 'Modification';
  // Informal must precede formal — "informal" contains "formal".
  if (/informal|lpo|emergency|urgent|local purchase/.test(m)) return 'Informal Tendering - PO';
  if (/formal|open tender|limited tender|closed tender|transvers/.test(m)) return 'Formal Tendering';
  if (dataset === 'POs' || dataset === 'LPOs') return 'Informal Tendering - PO';
  if (dataset === 'Contracts') return 'Formal Tendering';
  return 'Other';
}

/**
 * Resolve a transaction's RTM class through the three layers. `classOverrides` is
 * keyed by referenceNo; `classMap` by `${normalizeMechanismFacet(mechanism)}|${normalizeDatasetFacet(dataset)}`.
 */
export function resolveRtmClass(
  t: Pick<Transaction, 'referenceNo' | 'mechanism' | 'dataset' | 'panel'>,
  classOverrides: Record<string, string> = {},
  classMap: Record<string, string> = {},
): RtmClass {
  const ov = classOverrides[t.referenceNo];
  if (ov) return ov as RtmClass;
  const mapKey = `${normalizeMechanismFacet(t.mechanism)}|${normalizeDatasetFacet(t.dataset)}`;
  const mapped = classMap[mapKey];
  if (mapped) return mapped as RtmClass;
  return rtmClassDefault(t);
}
